import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

/** Web/desktop: the app reads as a centered phone-width column, never full-bleed. */
export const SEASON_PICKS_MAX_CONTENT_WIDTH = 680;

export const getSeasonPicksStyles = (theme: Theme) =>
  StyleSheet.create({
    screen: { flex: 1, backgroundColor: theme.background.primary, alignItems: 'center' },
    column: { flex: 1, width: '100%', maxWidth: SEASON_PICKS_MAX_CONTENT_WIDTH },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 12,
      paddingTop: 14,
      paddingBottom: 6,
    },
    headerTitle: {
      fontSize: theme.typography.h3.fontSize,
      fontWeight: '700',
      color: theme.black,
      flexShrink: 1,
    },
    backButton: { padding: 6 },
    /** Arrows + rail on one line so the rail is reachable with a mouse (AC-13.4). */
    railRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 4 },
    railScroll: { flex: 1 },
    railArrow: {
      padding: 6,
      borderRadius: 999,
      alignItems: 'center',
      justifyContent: 'center',
    },
    railArrowDisabled: { opacity: 0.3 },
    chipsRow: { paddingHorizontal: 8, paddingVertical: 8, gap: 8, flexDirection: 'row' },
    chip: {
      paddingHorizontal: 14,
      paddingVertical: 7,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.gray[300],
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    chipSelected: { backgroundColor: theme.primary, borderColor: theme.primary },
    /** Played matchweeks recede so the live/upcoming ones read first (AC-13.5). */
    chipFinished: { opacity: 0.6 },
    chipLabel: { fontSize: 13, color: theme.gray[500], fontWeight: '600' },
    chipLabelSelected: { color: theme.onPrimary },
    /** Live dot on a matchweek chip — paired with the label, never colour alone. */
    chipLiveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: theme.error },
    listContent: { padding: 12, paddingBottom: 48 },
    centerWrap: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      paddingBottom: 48,
    },
    emptyMonkey: { width: 100, height: 100, marginBottom: 16 },
    stateTitle: {
      fontSize: theme.typography.h3.fontSize,
      fontWeight: theme.typography.h3.fontWeight,
      color: theme.black,
      marginBottom: 8,
      textAlign: 'center',
    },
    stateSubtitle: {
      fontSize: theme.typography.bodySmall.fontSize,
      color: theme.gray[500],
      textAlign: 'center',
      lineHeight: 20,
      marginBottom: 24,
    },
    retryButton: {
      paddingHorizontal: 24,
      paddingVertical: 10,
      borderRadius: 20,
      backgroundColor: theme.primary,
    },
    retryLabel: { fontSize: 14, fontWeight: '700', color: theme.onPrimary },
    skeletonCard: {
      height: 128,
      borderRadius: 20,
      backgroundColor: theme.background.cardElevated,
      marginBottom: 12,
      opacity: 0.5,
    },
  });

export const getSeasonPickCardStyles = (theme: Theme) =>
  StyleSheet.create({
    // Chrome copied from the WC MatchCard (getMatchCardStyles, dark mode): radius 24,
    // borderless, deep shadow, 520px web cap, purple wave shell behind the content.
    card: {
      borderRadius: 24,
      backgroundColor: theme.background.card,
      overflow: 'hidden',
      width: '100%',
      maxWidth: 520,
      alignSelf: 'center',
      marginBottom: 14,
      borderWidth: 0,
      shadowColor: theme.shadow,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.4,
      shadowRadius: 12,
      elevation: 6,
      ...(Platform.OS === 'web' && {
        boxShadow: '0 8px 28px rgba(0,0,0,0.5)' as never,
      }),
    },
    /** WC "boosted" glow, reused for the pick-is-set state. */
    cardPicked: {
      borderWidth: 1,
      borderColor: `${theme.accent}88`,
      shadowColor: theme.accent,
      shadowOpacity: 0.45,
      shadowRadius: 18,
      ...(Platform.OS === 'web' && {
        boxShadow: `0 0 22px 3px ${theme.accent}66` as never,
      }),
    },
    cardLocked: { opacity: 0.85 },
    // --- Slice 4: settled-outcome treatment, ported from the WC MatchCardFinished
    // vocabulary (matchCardOutcome.helper.ts). Colour is always paired with a text
    // label so it is never the only channel (AC-12.2).
    /** 4px bar across the top of a settled card, tinted by outcome. */
    accentBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 4, zIndex: 2 },
    accentBarExact: { backgroundColor: theme.success },
    accentBarResult: { backgroundColor: theme.info },
    accentBarMiss: { backgroundColor: theme.error },
    outcomeLabel: { fontSize: 12, fontWeight: '700' },
    outcomeLabelExact: { color: theme.success },
    outcomeLabelResult: { color: theme.info },
    outcomeLabelMiss: { color: theme.error },
    pointsExact: { color: theme.success },
    pointsResult: { color: theme.info },
    /** A miss is +0 in error red — never success green (AC-12.3). */
    pointsMiss: { color: theme.error },
    /** Finished but unsettled: neutral, no outcome tint (AC-12.4). */
    pointsPending: {
      fontSize: 12,
      fontWeight: '600',
      fontStyle: 'italic',
      color: theme.gray[500],
    },
    /** Finished with no prediction — explicit, not a blank card (AC-12.5). */
    noPickLine: {
      fontSize: 12,
      color: theme.gray[500],
      marginTop: 10,
      textAlign: 'center',
      fontStyle: 'italic',
    },
    settledFooter: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      marginTop: 10,
    },
    cardContent: { zIndex: 1, paddingTop: 12, paddingBottom: 14, paddingHorizontal: 12 },
    /**
     * Three zones, not `space-between` over a variable child count (AC-16.5).
     *
     * The row's children come and go — kickoff or live, a save-error badge, a points badge,
     * "Scoring…", "Locks at kickoff" — so under `space-between` the x2's position was a
     * function of how wide its neighbours happened to be, and it moved as they appeared. The
     * two outer zones flex equally, which puts the centre zone on the row's midpoint whatever
     * the sides are showing.
     */
    metaRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
    },
    /**
     * `flex: 1` on both, so they split the leftover space evenly and the centre lands in the
     * middle. `minWidth: 0` lets a wide badge shrink rather than push the centre off it.
     */
    metaLeading: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 6 },
    metaTrailing: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 6,
    },
    /**
     * Sized by its content, so an empty centre — every season-pool card, and every finished
     * card — takes no width and the two side zones sit exactly where `space-between` put them
     * (AC-16.9).
     */
    metaCenter: { flexShrink: 0, alignItems: 'center', justifyContent: 'center' },
    /** WC stage badge pill — dark translucent on the purple band. */
    stageBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      backgroundColor: 'rgba(0,0,0,0.42)',
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 16,
    },
    stageBadgeText: { fontSize: 11, fontWeight: '600', color: theme.white },
    // Golden Ball control (specs/champions-league AC-4.5). Same vocabulary as the World Cup
    // match card's boost — Zap glyph plus ×2 — so a WC player recognises it immediately.
    boostButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.accent,
      backgroundColor: 'rgba(0,0,0,0.42)',
    },
    boostButtonActive: { backgroundColor: theme.accent, borderColor: theme.accent },
    // Dimmed rather than hidden: the rule stays visible on an ineligible match.
    boostButtonDisabled: { opacity: 0.35 },
    boostButtonPressed: { opacity: 0.7 },
    boostLabel: { fontSize: 11, fontWeight: '800', color: theme.accent },
    boostLabelActive: { color: theme.textOnCard },
    /**
     * Live chrome (Story 14). A live match used to be indistinguishable from a locked
     * pre-kickoff one: same badge, same lock glyph, just a score in the middle.
     */
    liveBadge: { backgroundColor: theme.error },
    liveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: theme.white },
    liveText: { fontSize: 11, fontWeight: '800', color: theme.white, letterSpacing: 0.4 },
    /** x2 marker beside a settled pick's points. Glyph + colour, never colour alone. */
    boostedMark: { fontSize: 11, fontWeight: '800', color: theme.accent },
    /** Round already spent, or kickoff passed. Colour AND opacity, never colour alone. */
    boostLabelDisabled: { color: theme.gray[400] },
    pointsText: { fontSize: 12, color: theme.success, fontWeight: '700' },
    saveError: { fontSize: 11, color: theme.error, fontWeight: '600' },
    mainRow: { flexDirection: 'row', alignItems: 'flex-start' },
    sideCol: { flex: 1, minWidth: 0, alignItems: 'center', gap: 10 },
    centerCol: { width: 86, alignItems: 'center', paddingTop: 4 },
    teamName: {
      fontSize: 15,
      fontWeight: '700',
      color: theme.white,
      textAlign: 'center',
    },
    /**
     * Dims the team names once the pick can no longer be edited — but NOT while the match is
     * live. The dimming says "there is nothing to do here", which is true of a finished
     * fixture and precisely wrong for the one game on the screen that is being played: a live
     * card is the one the player is watching, and greying its teams reads as the card having
     * gone inactive.
     */
    teamNameLocked: { color: theme.gray[400] },
    scoreBox: {
      backgroundColor: theme.background.inset,
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 7,
      minWidth: 72,
      alignItems: 'center',
    },
    scoreBoxEmpty: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderStyle: 'dashed',
    },
    /** A set pick keeps a gold ring on the box (card carries the WC boosted glow). */
    scoreBoxPicked: {
      borderWidth: 1,
      borderColor: theme.accent,
    },
    scoreText: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.black,
      fontVariant: ['tabular-nums'],
    },
    scoreTextEmpty: { color: theme.gray[400] },
    // The 90-minute / shootout line under the final score (AC-14.6). Deliberately quiet: the
    // final is the headline, and this states the number the pick was actually judged on.
    /**
     * The tie aggregate on a second leg (AC-6.9). Sits above the 90-minute line and reads a
     * little louder than it: the aggregate is who goes through, which is the bigger fact on a
     * knockout card, while the 90-minute line only explains this fixture's own verdict.
     */
    aggregateLine: {
      marginTop: 4,
      fontSize: 12,
      fontWeight: '800',
      color: theme.primaryLight,
      textAlign: 'center',
      fontVariant: ['tabular-nums'],
    },
    beyondNinetyBlock: { alignItems: 'center', marginTop: 4, gap: 1 },
    beyondNinetyPens: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.gray[600],
      fontVariant: ['tabular-nums'],
    },
    beyondNinetyRegulation: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[500],
      fontVariant: ['tabular-nums'],
    },
    steppersRow: { flexDirection: 'row', gap: 12 },
    stepperButton: {
      width: 34,
      height: 34,
      borderRadius: 17,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.inset,
      borderWidth: 1,
      borderColor: theme.gray[300],
    },
    stepperButtonPlus: { borderColor: theme.primary, backgroundColor: theme.primaryDark },
    stepperButtonPressed: { backgroundColor: theme.background.cardElevated },
    // --- Story 10: the card's action row. Full-bleed, so it is a sibling of
    // `cardContent` rather than a child — the hairline has to reach both edges the
    // way the WC MatchCard's does.
    actionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'stretch',
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.gray[200],
      zIndex: 1,
    },
    actionButton: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: 13,
      paddingHorizontal: 8,
    },
    actionButtonPressed: {
      opacity: 0.65,
      backgroundColor: theme.background.cardElevated,
    },
    actionDivider: {
      width: StyleSheet.hairlineWidth,
      alignSelf: 'stretch',
      backgroundColor: theme.gray[200],
    },
    actionIcon: { color: theme.gray[500] },
    actionText: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[600],
      letterSpacing: 0.1,
    },
    // Spacing lives on `settledFooter`, which now wraps this line.
    yourPickLine: {
      fontSize: 12,
      color: theme.gray[500],
      textAlign: 'center',
    },
  });
